// Shared proxy logic for Jev (TypeSafe AI's System One model), used by the Vite dev middleware
// (vite.config.ts) and the Cloudflare Pages Function (functions/api/jev.ts) — the same two hosts and
// the same discipline as `src/ai/proxyCore.ts`, which this module deliberately mirrors: the key never
// reaches the browser, the request shape is re-checked server-side rather than taken on the caller's
// word, and nothing here throws.
//
// The wire protocol is TypeSafe's HTTP API as documented at https://docs.typesafe.ai/api (read
// 2026-09-21, along with the State, Primitives, Choice, Noul, Confidence and JavaScript SDK pages —
// the docs are the source of truth, not this comment):
//
//   POST https://api.typesafe.ai/v1/systemone
//   Authorization: Bearer <key>
//   { "model": "jev-latest",
//     "state": <string | object | array of text>,
//     "questions": { "<id>": {"type":"noul"|"choice"|"score", "instructions": "...", "criteria": ...} } }
//
//   200 { "model": "jev-1.13.0",
//         "answers": { "<id>": {"type":"noul","noul":0.99}
//                            | {"type":"choice","choice":"x","probabilities":{...},"confidence":1.0}
//                            | {"type":"score","score":2.1,"legend":{...},"probabilities":{...},"confidence":0.8} },
//         "usage": { "input_tokens": 360, "output_tokens": 39 } }
//
//   401 invalid key · 422 validation · 429 rate limited · 529 overloaded
//
// Two things about that shape drive the code below. A Noul answer carries **no confidence field** —
// the docs are explicit that the single probability "fully describes the distribution" — so the
// normalised answer synthesises one from {yes, no} with TypeSafe's own confidence formula, which is
// what lets one interface show a Noul and a Choice side by side. And question ids are for our code
// only: "Question IDs are for code and are not sent to the model", so every question below carries its
// whole meaning in `instructions` and `criteria`.
//
// No SDK. `@typesafe-ai/sdk` is a Node package and this proxy has to run unchanged inside the
// Cloudflare Pages runtime, where the dependency would be one more thing to bundle for a single
// `fetch`; the HTTP contract above is small enough to hold directly, which also means this file shows
// a reader exactly what goes over the wire.

// ---- request and response types --------------------------------------------------------------------

export interface JevNoulQuestion {
  type: 'noul';
  instructions: string;
  /** Optional boundary clarification, per https://docs.typesafe.ai/primitives/noul. */
  criteria?: { true: string; false: string };
}

export interface JevChoiceQuestion {
  type: 'choice';
  instructions: string;
  /** Option name -> description. `null` is allowed by the SDK helpers for a self-describing option. */
  criteria: Record<string, string | null>;
}

export interface JevScoreQuestion {
  type: 'score';
  instructions: string;
  /** Two to ten level descriptions, lowest first. */
  criteria: string[];
}

export type JevQuestion = JevNoulQuestion | JevChoiceQuestion | JevScoreQuestion;

/** What the browser posts to `/api/jev`. The model id is not the caller's to choose. */
export interface JevProxyRequest {
  state: unknown;
  questions: Record<string, JevQuestion>;
}

/** One answer, normalised so a caller never has to branch on the primitive to read a probability. */
export interface JevAnswer {
  type: 'noul' | 'choice' | 'score';
  /** The chosen option for a choice; 'yes'/'no' for a noul; the level name for a score. */
  value: string;
  /** A distribution that sums to 1. A noul becomes `{yes: p, no: 1 - p}`. */
  probabilities: Record<string, number>;
  /** TypeSafe's own confidence for a choice/score; synthesised from the distribution for a noul. */
  confidence: number;
  /** The raw probability for a noul, so a caller that wants the number unrounded has it. */
  noul?: number;
  /** The raw numeric position for a score. */
  score?: number;
}

export interface JevUsage { inputTokens?: number; outputTokens?: number }

export type JevProxyResult =
  | { ok: true; model: string; answers: Record<string, JevAnswer>; usage?: JevUsage; latencyMs: number }
  | { ok: false; error: string; latencyMs: number };

export interface JevProxyEnv {
  apiKey?: string;
  /** Overridable for a test double or a staging endpoint; defaults to the documented production URL. */
  baseUrl?: string;
  /** Overridable model route; defaults to the documented `jev-latest`. */
  model?: string;
  /** Injectable fetch, so the request shaping can be tested without a network. */
  fetchImpl?: typeof fetch;
}

// ---- caps ------------------------------------------------------------------------------------------

export const JEV_URL = 'https://api.typesafe.ai/v1/systemone';
export const JEV_MODEL = 'jev-latest';

/**
 * Three seconds, and then the heuristic answers.
 *
 * Jev's published latency is 70-500 ms and this app's whole reason for using it is that it is fast
 * enough to sit inside a live fight. A budget six times the published worst case is generous; past it
 * the answer has stopped being an S-2 estimate and become an interruption, and there is a deterministic
 * estimator sitting right there. Same discipline as the language-model proxy's 6-second timeout
 * (`src/ai/client.ts`), scaled to a model that promises to be an order of magnitude quicker.
 */
export const JEV_TIMEOUT_MS = 3000;

/** Hard ceiling on the request body, in characters of JSON. State is capped at 32K tokens by the model;
 * this is the same 64 KB the language-model proxy applies, which is well inside that. */
export const MAX_BODY_BYTES = 64 * 1024;

/** Questions per request. The docs encourage asking independent questions together ("speculative
 * fan-out"); eight is more than this app's three plus a contact role each for the contacts on screen. */
export const MAX_QUESTIONS = 8;

/** Options per choice question. The documented limit is 255; nothing here needs more than a handful. */
export const MAX_CHOICE_OPTIONS = 32;

/** Published input price, $0.042 per million input tokens; output is free. Used only to print an
 * estimated spend in the calibration harness, never to make a decision. */
export const JEV_USD_PER_INPUT_TOKEN = 0.042 / 1_000_000;

// ---- request validation ----------------------------------------------------------------------------

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Whether a state value is one of the three shapes the docs allow: string, JSON object, array of text. */
export function isValidJevState(state: unknown): boolean {
  if (typeof state === 'string') return state.length > 0;
  if (Array.isArray(state)) return state.length > 0;
  return isPlainObject(state) && Object.keys(state).length > 0;
}

/** Whether one question is a shape the API will accept, checked here rather than discovered as a 422. */
export function isValidJevQuestion(q: unknown): q is JevQuestion {
  if (!isPlainObject(q)) return false;
  if (typeof q.instructions !== 'string' || q.instructions.length === 0) return false;
  if (q.type === 'noul') {
    if (q.criteria === undefined) return true;
    return isPlainObject(q.criteria)
      && typeof q.criteria.true === 'string' && typeof q.criteria.false === 'string';
  }
  if (q.type === 'choice') {
    if (!isPlainObject(q.criteria)) return false;
    const keys = Object.keys(q.criteria);
    if (keys.length < 2 || keys.length > MAX_CHOICE_OPTIONS) return false;
    return keys.every((k) => {
      const v = (q.criteria as Record<string, unknown>)[k];
      return v === null || typeof v === 'string';
    });
  }
  if (q.type === 'score') {
    return Array.isArray(q.criteria) && q.criteria.length >= 2 && q.criteria.length <= 10
      && q.criteria.every((c) => typeof c === 'string');
  }
  return false;
}

/** The shape check both hosts apply before a request is allowed to cost anything. */
export function isValidJevRequest(req: unknown): req is JevProxyRequest {
  if (!isPlainObject(req)) return false;
  if (!isValidJevState(req.state)) return false;
  if (!isPlainObject(req.questions)) return false;
  const ids = Object.keys(req.questions);
  if (ids.length === 0 || ids.length > MAX_QUESTIONS) return false;
  return ids.every((id) => isValidJevQuestion((req.questions as Record<string, unknown>)[id]));
}

export const INVALID_REQUEST_MESSAGE =
  `invalid request shape: expected {state, questions} with 1-${MAX_QUESTIONS} questions of type noul, choice or score`;

// ---- response normalisation -------------------------------------------------------------------------

/** TypeSafe's confidence formula, `(count x peak - 1) / (count - 1)`, for the noul case the API does
 * not answer with one. Duplicated from `calibrationMath.confidenceOf` rather than imported: this module
 * runs inside the Cloudflare Pages runtime and must not pull the calibration file in behind it. */
function confidenceFromDistribution(probabilities: Record<string, number>): number {
  const values = Object.values(probabilities);
  if (values.length <= 1) return 1;
  const peak = Math.max(...values);
  const c = (values.length * peak - 1) / (values.length - 1);
  return c < 0 ? 0 : c > 1 ? 1 : c;
}

function numberOr(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** Turns one raw API answer into the normalised shape above, or null when it is unreadable. */
export function normaliseAnswer(raw: unknown): JevAnswer | null {
  if (!isPlainObject(raw)) return null;

  if (raw.type === 'noul') {
    if (typeof raw.noul !== 'number' || !Number.isFinite(raw.noul)) return null;
    const p = Math.min(1, Math.max(0, raw.noul));
    const probabilities = { yes: p, no: 1 - p };
    return {
      type: 'noul',
      value: p >= 0.5 ? 'yes' : 'no',
      probabilities,
      confidence: confidenceFromDistribution(probabilities),
      noul: p,
    };
  }

  if (raw.type === 'choice') {
    if (typeof raw.choice !== 'string' || !isPlainObject(raw.probabilities)) return null;
    const probabilities: Record<string, number> = {};
    for (const [k, v] of Object.entries(raw.probabilities)) probabilities[k] = numberOr(v, 0);
    return {
      type: 'choice',
      value: raw.choice,
      probabilities,
      confidence: numberOr(raw.confidence, confidenceFromDistribution(probabilities)),
    };
  }

  if (raw.type === 'score') {
    if (!isPlainObject(raw.probabilities)) return null;
    const probabilities: Record<string, number> = {};
    for (const [k, v] of Object.entries(raw.probabilities)) probabilities[k] = numberOr(v, 0);
    // A score has no single named answer; the highest-probability level is the closest thing.
    let value = '';
    let peak = -Infinity;
    for (const [k, v] of Object.entries(probabilities)) {
      if (v > peak || (v === peak && k < value)) { value = k; peak = v; }
    }
    return {
      type: 'score',
      value,
      probabilities,
      confidence: numberOr(raw.confidence, confidenceFromDistribution(probabilities)),
      score: numberOr(raw.score, 0),
    };
  }

  return null;
}

// ---- the call ----------------------------------------------------------------------------------------

/**
 * One call to Jev, timed, capped and swallowed.
 *
 * Never throws and never rejects: every path out of here is a `JevProxyResult` carrying a latency, so
 * the caller can record a row in the AI audit whatever happened. A 429 or 529 is returned as an error
 * rather than retried — the documented advice is exponential backoff, which is the right answer for a
 * batch job and the wrong one inside a fight where the deterministic estimator is already the fallback.
 * The calibration harness does its own backoff (`tools/jev-calibrate.mts`).
 */
export async function proxyJev(req: JevProxyRequest, env: JevProxyEnv): Promise<JevProxyResult> {
  const started = Date.now();
  const elapsed = (): number => Date.now() - started;

  if (!env.apiKey) return { ok: false, error: 'missing JEV_API_KEY', latencyMs: elapsed() };

  const doFetch = env.fetchImpl ?? fetch;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), JEV_TIMEOUT_MS);

  try {
    const res = await doFetch(env.baseUrl ?? JEV_URL, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${env.apiKey}`,
      },
      body: JSON.stringify({
        model: env.model ?? JEV_MODEL,
        state: req.state,
        questions: req.questions,
      }),
      signal: controller.signal,
    });

    const text = await res.text();
    if (!res.ok) {
      return { ok: false, error: `jev ${res.status}: ${text.slice(0, 300)}`, latencyMs: elapsed() };
    }

    let data: unknown;
    try { data = JSON.parse(text); } catch {
      return { ok: false, error: 'jev: response was not JSON', latencyMs: elapsed() };
    }
    if (!isPlainObject(data) || !isPlainObject(data.answers)) {
      return { ok: false, error: 'jev: response had no answers map', latencyMs: elapsed() };
    }

    const answers: Record<string, JevAnswer> = {};
    for (const [id, raw] of Object.entries(data.answers)) {
      const normalised = normaliseAnswer(raw);
      if (normalised) answers[id] = normalised;
    }
    if (Object.keys(answers).length === 0) {
      return { ok: false, error: 'jev: no answer in the response could be read', latencyMs: elapsed() };
    }

    const usageRaw = isPlainObject(data.usage) ? data.usage : undefined;
    return {
      ok: true,
      model: typeof data.model === 'string' ? data.model : (env.model ?? JEV_MODEL),
      answers,
      usage: usageRaw
        ? {
          inputTokens: typeof usageRaw.input_tokens === 'number' ? usageRaw.input_tokens : undefined,
          outputTokens: typeof usageRaw.output_tokens === 'number' ? usageRaw.output_tokens : undefined,
        }
        : undefined,
      latencyMs: elapsed(),
    };
  } catch (err) {
    const aborted = err instanceof Error && (err.name === 'AbortError' || /abort/i.test(err.message));
    return {
      ok: false,
      error: aborted
        ? `jev: timed out after ${JEV_TIMEOUT_MS} ms`
        : err instanceof Error ? err.message : String(err),
      latencyMs: elapsed(),
    };
  } finally {
    clearTimeout(timer);
  }
}

// ---- the origin gate ----------------------------------------------------------------------------------

/** Where the app is deployed. Everything else asking for an answer is using us as somebody's API key. */
export const DEFAULT_ALLOWED_ORIGIN = 'https://windtunnel.pages.dev';

/**
 * Whether this request came from the page this function exists to serve.
 *
 * Identical rules to the language-model proxy's gate in `functions/api/llm.ts`, and for the identical
 * reason: a rate limit alone makes the endpoint an open relay for somebody else's questions on this
 * project's key. Both proxies share `origin.ts`, re-exported here for existing callers.
 *
 * Allowed: the deployment's own origin, any `*.windtunnel.pages.dev` preview branch, and localhost in
 * development. A request with neither `Origin` nor `Referer` is refused.
 */
export { originAllowed } from './origin';

// ---- .env loading (Node dev server only; a no-op under the Cloudflare Pages runtime) ------------------

const ENV_VARS = ['JEV_API_KEY', 'JEV_BASE_URL', 'JEV_MODEL'] as const;

/** Reads `KEY=value` lines out of `.env` in the working directory. Node-only (dynamic `node:fs` import
 * guarded by `typeof process`); under Cloudflare Pages the vars come from `context.env` instead. The
 * same shape as `loadDotEnv` in `src/ai/proxyCore.ts`, kept separate so this module has no import of
 * the language-model proxy. */
export async function loadJevDotEnv(): Promise<Partial<Record<(typeof ENV_VARS)[number], string>>> {
  if (typeof process === 'undefined') return {};
  try {
    const fs = await import('node:fs');
    if (!fs.existsSync('.env')) return {};
    const text = fs.readFileSync('.env', 'utf8');
    const out: Partial<Record<(typeof ENV_VARS)[number], string>> = {};
    for (const line of text.split('\n')) {
      const m = line.match(/^\s*([A-Z0-9_]+)\s*=\s*(.*?)\s*$/);
      if (m && (ENV_VARS as readonly string[]).includes(m[1])) {
        out[m[1] as (typeof ENV_VARS)[number]] = m[2];
      }
    }
    return out;
  } catch {
    return {};
  }
}

/** Resolves the key the dev middleware should use: what the caller passed, then the process
 * environment, then `.env`. Exported so the toggle's status line can say whether a key exists at all. */
export async function resolveJevEnv(apiKey?: string): Promise<JevProxyEnv> {
  const dotenv = await loadJevDotEnv();
  const procEnv = typeof process !== 'undefined' ? process.env : ({} as Record<string, string | undefined>);
  return {
    apiKey: apiKey ?? procEnv.JEV_API_KEY ?? dotenv.JEV_API_KEY,
    baseUrl: procEnv.JEV_BASE_URL ?? dotenv.JEV_BASE_URL,
    model: procEnv.JEV_MODEL ?? dotenv.JEV_MODEL,
  };
}

/**
 * Validates a body and resolves the environment, without calling anything. Both hosts start here.
 */
export async function prepareJevRequest(
  body: string, apiKey?: string,
): Promise<
  | { ok: false; status: number; body: string }
  | { ok: true; req: JevProxyRequest; env: JevProxyEnv }
> {
  if (body.length > MAX_BODY_BYTES) {
    return {
      ok: false,
      status: 413,
      body: JSON.stringify({ ok: false, error: `request body over ${MAX_BODY_BYTES} bytes` }),
    };
  }

  let parsed: unknown;
  try { parsed = JSON.parse(body); } catch {
    return { ok: false, status: 400, body: JSON.stringify({ ok: false, error: 'invalid JSON body' }) };
  }

  if (!isValidJevRequest(parsed)) {
    return { ok: false, status: 400, body: JSON.stringify({ ok: false, error: INVALID_REQUEST_MESSAGE }) };
  }

  return { ok: true, req: { state: parsed.state, questions: parsed.questions }, env: await resolveJevEnv(apiKey) };
}

/** Entry point the Vite dev middleware calls (`jevDevProxy` in vite.config.ts). */
export async function handleJevRequest(body: string, apiKey?: string): Promise<{ status: number; body: string }> {
  const prepared = await prepareJevRequest(body, apiKey);
  if (!prepared.ok) return { status: prepared.status, body: prepared.body };
  const result = await proxyJev(prepared.req, prepared.env);
  return { status: result.ok ? 200 : 502, body: JSON.stringify(result) };
}
